var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
import { IconButton, DatePicker, DateRangeType, addDays, DayOfWeek, initializeIcons, Callout, Text, mergeStyleSets } from '@fluentui/react';
import { addWeeks, subDays, differenceInCalendarDays, isBefore, startOfDay } from 'date-fns';
import { styles, dpStyles } from './CalendarBarComponent.styles';
import { useAppDispatch } from '../../store/app.store';
import { updateDates } from '../../store/Slices/DateNavigationSlice';
import React, { useEffect, useState } from 'react';
export function CalendarBarComponent({ selectedDate, setSelectedDate, startDate, endDate }) {
    initializeIcons();
    const gotoTodayIcon = { iconName: 'GotoToday' };
    const chevronLeftIcon = { iconName: 'ChevronLeft' };
    const chevronRightIcon = { iconName: 'ChevronRight' };
    const numberOfWeeks = 2;
    const dispatch = useAppDispatch();
    const [formattedDate, setFormattedDate] = useState('');
    // Info icon callout state
    const [isCalloutVisible, setIsCalloutVisible] = useState(false);
    const [infoIconId] = useState('info-icon-calendar');
    const today = new Date();
    const minDate = addDays(today, -7 * numberOfWeeks);
    const maxDate = addDays(today, 7 * 12);
    const initialStartDate = startDate ? startDate : today;
    const effectiveStartDate = (startDate && endDate) ? startDate : initialStartDate;
    useEffect(() => {
        if (!selectedDate) {
            setSelectedDate(effectiveStartDate);
        }
    }, []);
    useEffect(() => {
        if (selectedDate) {
            const newFormattedDate = formatDate(selectedDate);
            setFormattedDate(newFormattedDate);
        }
    }, [selectedDate]);
    const computeRange = (date) => {
        let startDateString = date.toDateString();
        return {
            startDate: startDateString,
            numberOfWeeks: numberOfWeeks,
        };
    };
    const formatDate = (date) => {
        const range = computeRange(date);
        dispatch(updateDates(range));
        let start = date;
        let end;
        if (isBefore(startOfDay(start.getDate()), startOfDay(today.getDate()))) {
            // selected date in the past, so limit end to today
            end = subDays(new Date(), 0); // corrected to include today
            const daysDiff = differenceInCalendarDays(end, start);
            if (daysDiff >= 14) {
                end = addDays(start, 13);
            }
        }
        else {
            end = subDays(addWeeks(new Date(range.startDate), numberOfWeeks), 1);
        }
        return (start.toLocaleString('default', { month: 'short' }) +
            ' ' +
            start.toLocaleString('default', { day: 'numeric' }) +
            ' - ' +
            end.toLocaleString('default', { month: 'short' }) +
            ' ' +
            end.toLocaleString('default', { day: 'numeric' }) +
            ', ' +
            end.toLocaleString('default', { year: 'numeric' }));
    };
    const setDate = (newDate) => {
        if (newDate) {
            if (newDate < minDate) {
                setSelectedDate(minDate);
            }
            else if (newDate > maxDate) {
                setSelectedDate(maxDate);
            }
            else {
                setSelectedDate(newDate);
            }
        }
    };
    const goPrevious = React.useCallback(() => {
        if (selectedDate) {
            const newDate = addDays(selectedDate, -(7 * numberOfWeeks));
            setDate(newDate);
        }
    }, [selectedDate]);
    const goNext = React.useCallback(() => {
        if (selectedDate) {
            const newDate = addDays(selectedDate, 7 * numberOfWeeks);
            setDate(newDate);
        }
    }, [selectedDate]);
    const goToday = React.useCallback(() => {
        const fiscalWeekStartOfCurrentWeek = new Date();
        setDate(fiscalWeekStartOfCurrentWeek); //go today
    }, []);
    const handleDatePickerChange = React.useCallback((newDate) => {
        if (newDate) {
            let newFiscalWeekStartDate = newDate;
            setDate(newFiscalWeekStartDate);
        }
    }, [setSelectedDate]);
    const toggleIsCalloutVisible = () => {
        setIsCalloutVisible(!isCalloutVisible);
    };
    return (React.createElement("div", { style: styles.container },
        React.createElement(IconButton, { iconProps: gotoTodayIcon, "aria-label": "GotoToday", onClick: goToday }),
        React.createElement(IconButton, { iconProps: chevronLeftIcon, "aria-label": "previousWeek", onClick: goPrevious, disabled: !selectedDate || startOfDay(addDays(selectedDate, -7 * numberOfWeeks)) < startOfDay(minDate) }),
        React.createElement(IconButton, { iconProps: chevronRightIcon, "aria-label": "nextWeek", onClick: goNext, disabled: !selectedDate || startOfDay(addDays(selectedDate, 7 * numberOfWeeks)) > startOfDay(maxDate) }),
        "      ",
        React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
            React.createElement(DatePicker, { styles: dpStyles, value: selectedDate, onSelectDate: handleDatePickerChange, showMonthPickerAsOverlay: true, firstDayOfWeek: DayOfWeek.Saturday, ariaLabel: "Select a date", isMonthPickerVisible: false, formatDate: () => formattedDate, showGoToToday: false, borderless: true, minDate: minDate, maxDate: maxDate, calendarProps: {
                    dateRangeType: DateRangeType.Week
                }, textField: {
                    iconProps: { iconName: 'ChevronDown' },
                    styles: {
                        field: {
                            fontWeight: 600,
                        },
                        fieldGroup: {
                            // Ensure field has appropriate focus styling
                            selectors: {
                                '&:focus-within': {
                                    outline: '2px solid black',
                                    outlineOffset: '2px'
                                }
                            }
                        }
                    },
                    'aria-label': 'Calendar button to select date range'
                } }),
            React.createElement(IconButton, { iconProps: { iconName: 'Info' }, id: infoIconId, onClick: toggleIsCalloutVisible, className: calloutStyles.infoIcon, ariaLabel: 'Click to see schedule change information' }),
            isCalloutVisible && (React.createElement(Callout, { className: calloutStyles.callout, role: "alert", gapSpace: 0, target: `#${infoIconId}`, onDismiss: toggleIsCalloutVisible, setInitialFocus: true },
                React.createElement(Text, { className: calloutStyles.message }, "Ensure to submit Schedule changes for current 14 day Calendar date range before moving to a different range, otherwise the edits will not be saved.")))),
        "    "));
}
const calloutStyles = mergeStyleSets({
    infoIcon: {
        fontSize: '16px',
        color: '#4177CF',
        cursor: 'pointer',
        marginLeft: '8px',
    },
    callout: {
        width: 300,
        maxWidth: '90%',
        padding: '12px 16px 16px 16px',
    },
    message: {
        fontSize: '14px',
        color: '#323130',
        lineHeight: '20px',
    },
});
//# sourceMappingURL=CalendarBarComponent.js.map